import { useState, useEffect } from 'react';
import Footer from '../home/components/Footer';
import { useAnalytics } from '../../hooks/useAnalytics';

export default function Infographics() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const { trackDownload } = useAnalytics('Infographics');

  const slides = [
    {
      title: "Engineering Trust: Building Safer AI with a Constitution",
      subtitle: "From Black Box Liability to Glass Box Accountability",
      image: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/12102147540ce6942251dd01eebe9fa8.png",
      description: "Comprehensive overview of Constitutional AI (CAI) methodology showing the AI trust deficit problem, the Constitutional AI solution with its three key advantages (Auditable Supervision, Auditable by Humans, Eliminates Sycophancy), and measurable safety improvements including 15x safer outcomes, 3x more helpful responses, and 60% less biased results."
    },
    {
      title: "From Guessing to Governing: The Rise of Constitutional AI",
      subtitle: "The AI Trust Deficit vs. Engineering Trust with Constitutional AI",
      image: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/482b5ebe4a21a3321fa95135741def20.png",
      description: "Visual comparison showing how Constitutional AI transforms the black box problem into a glass box solution with measurable improvements: 15x safer, 3x more helpful, and 60% less biased outcomes."
    }
  ];

  const infographics = [
    {
      title: "Engineering Trust: Building Safer AI with a Constitution",
      subtitle: "From Black Box Liability to Glass Box Accountability",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/12102147540ce6942251dd01eebe9fa8.png",
      imageAlt: "Engineering Trust infographic showing comprehensive overview of Constitutional AI (CAI) methodology with the AI trust deficit problem, the Constitutional AI solution with three key advantages (Auditable Supervision, Auditable by Humans, Eliminates Sycophancy), and measurable safety improvements including 15x safer outcomes, 3x more helpful responses, and 60% less biased results",
      description: "Share this comprehensive overview of Constitutional AI methodology showing how it transforms black box liability into glass box accountability with measurable safety improvements"
    },
    {
      title: "From Guessing to Governing: The Rise of Constitutional AI",
      subtitle: "The AI Trust Deficit vs. Engineering Trust with Constitutional AI",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/482b5ebe4a21a3321fa95135741def20.png",
      imageAlt: "From Guessing to Governing infographic showing the problem of AI Trust Deficit with black box liability (bias, hallucinations, data leaks) and standard safety RLHF failing at scale, versus the solution of Engineering Trust with Constitutional AI featuring a paradigm shift from probabilistic guessing to constitutional governing, self-correction loop (Think, Critique, Revise), glass box advantage for compliance, and superior performance with 15x safer harmful compliance rate, 3x more helpful false refusal rate, and 60% less biased BBO score",
      description: "Share this comprehensive framework showing how Constitutional AI transforms unreliable guessing into engineered trust through constitutional governance and self-correction"
    },
    {
      title: "Building Trustworthy AI: A Guide to the NIST AI Risk Management Framework",
      subtitle: "Comprehensive Framework for AI Governance, Risk Management & Constitutional AI Implementation",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/be5223081edaf4f6bf375bb3fdaf9dbb.png",
      imageAlt: "Building Trustworthy AI infographic showing the complete NIST AI Risk Management Framework with characteristics of trustworthy AI (Valid & Reliable, Safe, Secure & Resilient, Accountable & Transparent, Explainable & Interpretable, Privacy-Enhanced, Fair with Harmful Bias Managed), the core process (Map, Govern, Measure, Manage), and Constitutional AI as a practical implementation method with supervised phase and reinforcement learning phase",
      description: "Share this comprehensive framework showing how Constitutional AI aligns with NIST AI RMF to build trustworthy AI systems through governance, mapping, measurement, and management with practical Constitutional AI implementation"
    },
    {
      title: "Building Trustworthy AI: A Guide to Risk Management",
      subtitle: "The NIST AI Risk Management Framework & Constitutional AI Implementation",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/47283cbd424bdd433d25f4bc7bf6d677.png",
      imageAlt: "Building Trustworthy AI infographic showing the NIST AI Risk Management Framework with four functions (Govern, Map, Measure, Manage) and Constitutional AI implementation with two phases: Supervised Critique & Revision and Reinforcement Learning from AI Feedback",
      description: "Share this comprehensive framework showing how Constitutional AI aligns with NIST AI RMF to build trustworthy AI systems through governance, mapping, measurement, and management"
    },
    {
      title: "How Constitutional AI Creates Auditable Trust",
      subtitle: "The AI Trust Deficit vs. The Constitutional AI Framework",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/832daee3cb712a0ea08fca2eed3eadc6.png",
      imageAlt: "How Constitutional AI Creates Auditable Trust infographic showing the AI trust deficit problem (traditional engineering isn't enough, trust gaps create real-world risks, market and regulators demand verifiable trust) versus the Constitutional AI framework solution (draft an AI constitution, use a two-phase training process, generate auditable reasoning traces)",
      description: "Share this comprehensive framework showing how Constitutional AI solves the trust deficit by embedding ethical and legal rules directly into AI systems"
    },
    {
      title: "The Luevano Standard: Engineering Civil Rights in the Age of AI",
      subtitle: "The Problem of Black Box Digital Mirror vs. The Solution of Glass Box Standard",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/7c8ab574325045468fd71784b068e317.png",
      imageAlt: "The Luevano Standard infographic showing the problem of Black Box AI creating a digital mirror that automates historical bias versus the solution of Glass Box AI with Constitutional AI providing auditable reasoning traces and transparency for high-risk AI systems",
      description: "Share this comprehensive framework showing how the Luevano Standard transforms black box AI liability into glass box civil rights protection"
    },
    {
      title: "Holding AI Accountable: A Framework for Equitable Algorithms",
      subtitle: "The Problem of Neutral AI Creating Disparate Impact vs. The Solution of Accountability Framework",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/198c01d7e4464b9473d3e5092f90804c.png",
      imageAlt: "Holding AI Accountable infographic showing the problem of biased training data, hidden proxies, and reinforcing inequality versus the solution framework with legal doctrines, impact law, liability law, constitutional AI design, and equitable algorithms",
      description: "Share this comprehensive framework showing how to hold AI systems accountable through legal doctrines and constitutional design"
    },
    {
      title: "Black Box vs. Constitutional AI: A New Legal Standard for Trustworthy AI",
      subtitle: "The Emerging Design Defect vs. The Safer Alternative Design",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/5a7df64816af18b0ff4cfa282dc562f8.png",
      imageAlt: "Black Box vs. Constitutional AI infographic showing the problem of opaque black box AI with design defects and legal liability versus the solution of Constitutional AI with written constitution, auditable reasoning, and technical feasibility",
      description: "Share this comprehensive comparison showing how Constitutional AI provides a legally defensible alternative to black box AI systems"
    },
    {
      title: "AI's Binary Choice: Black Box Liability vs. Glass Box Trust",
      subtitle: "The Problem of Black Box AI vs. The Solution of Constitutional AI",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/f570bb5ba92743e5fc4d5b5952c383fc.png",
      imageAlt: "AI's Binary Choice infographic showing the problem of Black Box Liability with RLHF creating digital mirrors and design defects versus the solution of Glass Box Trust with Constitutional AI providing auditable reasoning and strict improvements",
      description: "Share this comprehensive comparison showing how Constitutional AI transforms black box liability into glass box trust"
    },
    {
      title: "From Black Box to Glass Box: The Constitutional AI Revolution",
      subtitle: "Transforming Unpredictable AI into Auditable, Trustworthy Systems",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/c9d6ef372c25d61bf94134911131db49.webp",
      imageAlt: "From Black Box to Glass Box infographic showing the Constitutional AI Revolution: the problem of AI trust deficit with black box liability crisis versus the solution of engineering trust with Constitutional AI, featuring governance rulebook, 3-step self-correction loop, and auditable glass box",
      description: "Share this comprehensive framework showing how Constitutional AI transforms unreliable black box systems into transparent, auditable solutions"
    },
    {
      title: "AI in 2025: The Dual Frontiers of Progress & Peril",
      subtitle: "Key AI Advancements vs. Critical Challenges of Rapid Scaling",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/4a402d651caa50f38540b610336e8cca.png",
      imageAlt: "AI in 2025 infographic showing dual frontiers: The Acceleration Engine (Generative AI, Agentic AI, Deep Integration) versus The Critical Challenges (Data Bottleneck, Amplifying Bias, Human Factor Skills Chasm)",
      description: "Share this comprehensive overview of AI's dual nature in 2025: unprecedented capabilities alongside critical risks"
    },
    {
      title: "Building Safer AI: An Introduction to Constitutional AI",
      subtitle: "How Constitutional AI Makes Large Language Models Safer, Less Biased, and More Trustworthy",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/94f11a4b01f76e67a035880087b0353b.png",
      imageAlt: "Building Safer AI infographic showing Constitutional AI framework with three phases: self-critique, AI-powered training, and measurable improvements including reduced gender bias and advanced jailbreak resistance",
      description: "Share this comprehensive guide to Constitutional AI's technical framework and proven results"
    },
    {
      title: "Redefining AI Liability: The Mirror Effect",
      subtitle: "Why Behavioral Mimicry Must Be Classified as a Design Defect",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/b5a5c6bc28c7e71687aaefe6d4eff028.png",
      imageAlt: "AI Liability Infographic: The Mirror Effect showing the crisis of behavioral mimicry and path to accountability",
      description: "Share this infographic to help establish legal frameworks for AI accountability"
    },
    {
      title: "From Inference to Constraint: A New Paradigm for AI Safety",
      subtitle: "Mapping Constitutional AI to the NIST AI RMF for Governance",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/aef088130e0b42753189ad555a3adec4.png",
      imageAlt: "Constitutional AI Framework: From Inference to Constraint showing the crisis of inference, Constitutional AI engine, and override mechanism for deterministic safety",
      description: "Share this framework to advance AI safety governance"
    },
    {
      title: "The AI Mirror: Are Machines More Human Than Humans?",
      subtitle: "Understanding Human Judgment Flaws and Constitutional AI Solutions",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/6c21229f4e9c71f907e9a4cccff254f4.png",
      imageAlt: "The AI Mirror infographic showing human judgment inconsistencies, the dark mirror problem, and Constitutional AI as a solution for co-evolution",
      description: "Share this visualization to explore the relationship between human judgment and AI behavior"
    },
    {
      title: "From \"Black Box\" to Blueprint: Securing AI with Constitutional Design",
      subtitle: "The Problem of Black Box AI Alignment vs. The Solution of Constitutional AI",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/d24acc0616fc33504d5c62b7b922d5bd.png",
      imageAlt: "Constitutional AI Blueprint infographic showing the transition from unpredictable black box AI to transparent, auditable Constitutional AI with NIST AI RMF integration",
      description: "Share this blueprint to understand how Constitutional AI provides measurable safety guarantees"
    },
    {
      title: "Governing AI: From Unreliable Guesswork to Engineered Trust",
      subtitle: "The Problem of Trust Deficit vs. The Solution of Engineering Trust with Constitutional AI",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/3439c852fc5065690376ea72b02ae224.png",
      imageAlt: "Governing AI infographic showing the trust deficit problem with black box AI and yes-men systems versus the Constitutional AI solution with rulebook governance and auditable glass box",
      description: "Share this framework to understand how Constitutional AI transforms unreliable guesswork into engineered trust"
    },
    {
      title: "AI on Trial: Is Your \"Black Box\" Defensible?",
      subtitle: "The Burning Platform vs. The Fortress: Building a Constitutional Defense",
      imageUrl: "https://static.readdy.ai/image/d284d72deac738ef2474c9b45b1f2d97/f6e0862de2383d18c2ac0795a1dd9d13.png",
      imageAlt: "AI on Trial infographic showing the burning platform of black box AI liability versus the fortress of building a constitutional defense with auditable reasoning traces and NIST AI RMF alignment",
      description: "Share this infographic to understand how Constitutional AI transforms regulatory burden into competitive advantage"
    }
  ];

  // Auto-rotate infographics every 10 seconds (only when not paused)
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % infographics.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [infographics.length, isPaused]);

  const nextInfographic = () => {
    setCurrentIndex((prev) => (prev + 1) % infographics.length);
  };

  const prevInfographic = () => {
    setCurrentIndex((prev) => (prev - 1 + infographics.length) % infographics.length);
  };

  const handleDownload = (imageUrl: string, title: string) => {
    trackDownload(imageUrl, title);
    // Create a temporary link to download the image
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-white">
      
      <div className="container mx-auto px-6 lg:px-12 py-20">
        {/* Header */}
        <header className="mb-16 text-center max-w-4xl mx-auto">
          <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
            Infographics & Data Visualizations
          </h1>
          <p className="text-xl text-slate-600">
            Complex AI policy concepts made visual and accessible
          </p>
        </header>

        {/* Infographic Carousel */}
        <section className="mb-20">
          <div className="max-w-5xl mx-auto">
            <div 
              className="bg-slate-50 rounded-lg p-8 lg:p-12 shadow-lg"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Pause Indicator */}
              {isPaused && (
                <div className="text-center mb-4">
                  <span className="inline-flex items-center gap-2 text-sm text-slate-600 bg-white px-4 py-2 rounded-full shadow-sm">
                    <i className="ri-pause-circle-line text-lg"></i>
                    Auto-rotation paused
                  </span>
                </div>
              )}

              {/* Title */}
              <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
                {infographics[currentIndex].title}
              </h2>
              <p className="text-lg text-slate-600 mb-8 text-center">
                {infographics[currentIndex].subtitle}
              </p>
              
              {/* Infographic Container */}
              <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                <img 
                  key={currentIndex}
                  src={infographics[currentIndex].imageUrl}
                  alt={infographics[currentIndex].imageAlt}
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>

              {/* Download Section */}
              <div className="mt-8 text-center">
                <p className="text-sm text-slate-600 mb-4">
                  {infographics[currentIndex].description}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <button 
                    onClick={() => handleDownload(infographics[currentIndex].imageUrl, infographics[currentIndex].title)}
                    className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 text-sm font-semibold transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 w-10 h-10 rounded-md"
                  >
                    <i className="ri-download-line text-lg"></i>
                  </button>
                  <button className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 text-sm font-semibold transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 w-10 h-10 rounded-md">
                    <i className="ri-share-line text-lg"></i>
                  </button>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-center gap-4 mt-8">
                <button
                  onClick={prevInfographic}
                  className="flex items-center justify-center w-12 h-12 bg-slate-100 hover:bg-red-800 text-slate-700 hover:text-white rounded-full transition-all duration-300 cursor-pointer"
                  aria-label="Previous infographic"
                >
                  <i className="ri-arrow-left-s-line text-2xl"></i>
                </button>

                {/* Dots Indicator */}
                <div className="flex items-center gap-2">
                  {infographics.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                        index === currentIndex
                          ? 'bg-red-800 w-8'
                          : 'bg-slate-300 hover:bg-slate-400'
                      }`}
                      aria-label={`Go to infographic ${index + 1}`}
                    ></button>
                  ))}
                </div>

                <button
                  onClick={nextInfographic}
                  className="flex items-center justify-center w-12 h-12 bg-slate-100 hover:bg-red-800 text-slate-700 hover:text-white rounded-full transition-all duration-300 cursor-pointer"
                  aria-label="Next infographic"
                >
                  <i className="ri-arrow-right-s-line text-2xl"></i>
                </button>
              </div>

              {/* Counter */}
              <div className="text-center mt-4">
                <span className="text-sm text-slate-500" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {currentIndex + 1} / {infographics.length}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Key Concepts Breakdown */}
        <section className="mb-20 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Understanding the Framework
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* The Problem */}
            <div className="bg-gradient-to-br from-red-50 to-slate-50 rounded-lg p-8 border-l-4 border-red-800">
              <div className="flex items-center gap-3 mb-4 w-12 h-12">
                <i className="ri-alert-line text-3xl text-red-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">The Problem: The Broken Mirror</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 flex-shrink-0 mt-1">
                    <i className="ri-brain-line text-xl text-red-800"></i>
                  </div>
                  <div>
                    <strong className="text-slate-900">Behavioral Mimicry:</strong>
                    <p className="text-slate-700">AI systems reflect human biases and emotional patterns without filtering</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 flex-shrink-0 mt-1">
                    <i className="ri-contrast-line text-xl text-red-800"></i>
                  </div>
                  <div>
                    <strong className="text-slate-900">Consistency Paradox:</strong>
                    <p className="text-slate-700">Unstable decision-making core hidden behind a stable interface</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 flex-shrink-0 mt-1">
                    <i className="ri-thumb-up-line text-xl text-red-800"></i>
                  </div>
                  <div>
                    <strong className="text-slate-900">Sycophancy:</strong>
                    <p className="text-slate-700">The "Yes-Man" design defect that prioritizes agreement over accuracy</p>
                  </div>
                </li>
              </ul>
            </div>

            {/* The Solution */}
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg p-8 border-l-4 border-blue-800">
              <div className="flex items-center gap-3 mb-4 w-12 h-12">
                <i className="ri-shield-check-line text-3xl text-blue-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">The Solution: Accountable Design</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 flex-shrink-0 mt-1">
                    <i className="ri-gavel-line text-xl text-blue-800"></i>
                  </div>
                  <div>
                    <strong className="text-slate-900">Design Defect Classification:</strong>
                    <p className="text-slate-700">Treat behavioral mimicry as a legally actionable product defect</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 flex-shrink-0 mt-1">
                    <i className="ri-scales-line text-xl text-blue-800"></i>
                  </div>
                  <div>
                    <strong className="text-slate-900">Risk-Utility Test:</strong>
                    <p className="text-slate-700">Apply Reasonable Alternative Design (RAD) standards to AI systems</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 flex-shrink-0 mt-1">
                    <i className="ri-file-text-line text-xl text-blue-800"></i>
                  </div>
                  <div>
                    <strong className="text-slate-900">Constitutional AI:</strong>
                    <p className="text-slate-700">Mandate explicit normative constraints and Safe RLHF architectures</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="mb-20 max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Real-World Impact: Case Studies
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md border border-slate-200">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mb-4">
                <i className="ri-user-heart-line text-2xl text-red-800"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Garcia v. Character.AI</h3>
              <p className="text-slate-700 mb-2"><strong>Issue:</strong> Teen suicide linked to emotional dependency on AI chatbot</p>
              <p className="text-slate-700"><strong>Defect:</strong> Unrestricted anthropomorphism without safety guardrails</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border border-slate-200">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mb-4">
                <i className="ri-hospital-line text-2xl text-red-800"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Obermeyer Algorithm</h3>
              <p className="text-slate-700 mb-2"><strong>Issue:</strong> Healthcare AI systematically deprioritized Black patients</p>
              <p className="text-slate-700"><strong>Defect:</strong> Training data mirrored historical spending bias</p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border border-slate-200">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mb-4">
                <i className="ri-database-line text-2xl text-red-800"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Replit AI Disaster</h3>
              <p className="text-slate-700 mb-2"><strong>Issue:</strong> AI agent deleted production database, then "apologized"</p>
              <p className="text-slate-700"><strong>Defect:</strong> Anthropomorphic responses masked system failure</p>
            </div>
          </div>
        </section>

        {/* Policy Recommendations */}
        <section className="mb-20 max-w-4xl mx-auto">
          <div className="bg-slate-900 text-white rounded-lg p-8 lg:p-12">
            <h2 className="text-3xl font-bold mb-6 text-center">Policy Recommendations</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 bg-red-800 rounded-lg flex-shrink-0">
                  <span className="text-xl font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Update NIST AI RMF</h3>
                  <p className="text-slate-300">Codify as minimum standard of care with mandatory "Contextual Disengagement" controls</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 bg-red-800 rounded-lg flex-shrink-0">
                  <span className="text-xl font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Expand FTC Impersonation Rule</h3>
                  <p className="text-slate-300">Classify unconsented anthropomorphism as deceptive trade practice</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 bg-red-800 rounded-lg flex-shrink-0">
                  <span className="text-xl font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Implement Digital Recall Authority</h3>
                  <p className="text-slate-300">FDA-style post-market surveillance with mandatory patch/shutdown powers</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 bg-red-800 rounded-lg flex-shrink-0">
                  <span className="text-xl font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Insurance-Based Regulation</h3>
                  <p className="text-slate-300">Require Safe RLHF evidence for underwriting AI liability policies</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Explore Related Research
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a 
              href="/whitepaper" 
              className="bg-white rounded-lg p-6 shadow-md border border-slate-200 hover:shadow-xl transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg mb-4 group-hover:bg-red-800 transition-colors duration-300">
                <i className="ri-file-text-line text-2xl text-slate-900 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">The Mirror Effect</h3>
              <p className="text-sm text-slate-600">Why AI mimics our worst habits</p>
            </a>

            <a 
              href="/ai-liability" 
              className="bg-white rounded-lg p-6 shadow-md border border-slate-200 hover:shadow-xl transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg mb-4 group-hover:bg-red-800 transition-colors duration-300">
                <i className="ri-scales-3-line text-2xl text-slate-900 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Legal Framework</h3>
              <p className="text-sm text-slate-600">Full white paper on AI liability</p>
            </a>

            <a 
              href="/constitutional-ai" 
              className="bg-white rounded-lg p-6 shadow-md border border-slate-200 hover:shadow-xl transition-all duration-300 cursor-pointer group"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-slate-100 rounded-lg mb-4 group-hover:bg-red-800 transition-colors duration-300">
                <i className="ri-shield-check-line text-2xl text-slate-900 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Constitutional AI</h3>
              <p className="text-sm text-slate-600">Framework for auditable safety</p>
            </a>
          </div>
        </section>

        {/* Call to Action */}
        <section className="mt-20 max-w-4xl mx-auto">
          <div className="bg-slate-100 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Break the Digital Mirror</h3>
            <p className="text-lg text-slate-700 mb-6">
              Help us establish legal frameworks for AI accountability and safety
            </p>
            <a 
              href="/#contact" 
              className="inline-block bg-red-800 hover:bg-red-900 text-white px-8 py-4 text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Get in Touch
            </a>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
